import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
  ClickAwayListener,
  Fade,
  alpha,
  useTheme,
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useAppSelector } from '@/store/hooks';
import { fetchReportLogList } from '../report-logs/queries';
import {
  JOB_EXPIRY_MS,
  ReportJob,
  reportJobs,
  useReportJobs,
} from './reportJobsStore';
// The viewer (and its table library) loads the first time a report is opened, not with every page.
const loadViewer = () => import('../viewer/ReportViewer');
const LazyReportViewer = lazy(loadViewer);
const ReportViewer = () => {
  const open = useReportJobs((s) => !!s.viewer);
  const jobCount = useReportJobs((s) => s.jobs.length);
  const [used, setUsed] = useState(open);
  useEffect(() => {
    if (open) setUsed(true);
  }, [open]);
  // fetch it ahead while reports are running, so opening one is instant
  useEffect(() => {
    if (jobCount) void loadViewer();
  }, [jobCount]);
  if (!used) return null;
  return (
    <Suspense fallback={null}>
      <LazyReportViewer />
    </Suspense>
  );
};

dayjs.extend(relativeTime);

// 15 s while a report is queued; paused while the tab is hidden, checked again on return
const POLL_MS = 15000;
/** the scheduler's log row can be stamped slightly before our submit time */
const CLOCK_SKEW_MS = 5 * 60 * 1000;

interface LogRow {
  CRLOG_ID: number;
  CRLOG_STATUS: string;
  CRLOG_CR_DT: string;
  CRLOG_FILE_PATH: string | null;
  CRLOG_EXCEL_PATH: string | null;
}

const elapsed = (from: number, now: number) => {
  const secs = Math.max(0, Math.round((now - from) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return mins < 60 ? `${mins}m ${secs % 60}s` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

/** Links queued jobs to their log rows and marks them ready. */
const useJobPolling = () => {
  const dispatch = useDispatch();
  const jobs = useReportJobs((s) => s.jobs);
  const pending = useMemo(() => jobs.filter((j) => j.status === 'queued'), [jobs]);
  const repIds = useMemo(() => [...new Set(pending.map((j) => j.repId))], [pending]);

  const results = useQueries({
    queries: repIds.map((repId) => ({
      queryKey: ['report-log', String(repId)],
      queryFn: () => fetchReportLogList({ rep_id: String(repId) }),
      refetchInterval: POLL_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    })),
  });

  // re-run matching whenever any poll returns
  const stamp = results.map((r) => r.dataUpdatedAt).join(',');

  useEffect(() => {
    const now = Date.now();
    const all = reportJobs.getState().jobs;
    const claimed = new Set(all.map((j) => j.logId).filter(Boolean));

    repIds.forEach((repId, i) => {
      const logs: LogRow[] = results[i]?.data?.data?.result ?? [];
      if (!Array.isArray(logs)) return;

      all
        .filter((j) => j.repId === repId && j.status === 'queued')
        .sort((a, b) => a.submittedAt - b.submittedAt)
        .forEach((job) => {
          let logId = job.logId;
          if (!logId) {
            const candidate = logs
              .filter(
                (log) =>
                  !job.knownLogIds.includes(log.CRLOG_ID) &&
                  !claimed.has(log.CRLOG_ID) &&
                  new Date(log.CRLOG_CR_DT).getTime() >= job.submittedAt - CLOCK_SKEW_MS
              )
              .sort((a, b) => a.CRLOG_ID - b.CRLOG_ID)[0];
            if (candidate) {
              logId = candidate.CRLOG_ID;
              claimed.add(logId);
              reportJobs.patch(job.id, { logId });
            }
          }

          const log = logs.find((l) => l.CRLOG_ID === logId);
          // deleted from Run history (soft delete 'D', or gone from the list)
          if (logId && (!log || log.CRLOG_STATUS === 'D')) {
            reportJobs.remove(job.id);
            return;
          }
          if (log?.CRLOG_STATUS === 'C') {
            reportJobs.patch(job.id, {
              status: 'ready',
              pdfUrl: log.CRLOG_FILE_PATH || null,
              excelUrl: log.CRLOG_EXCEL_PATH || null,
            });
            dispatch({
              type: 'OPEN_SNACK_TOAST',
              payload: {
                title: 'Report ready',
                message: `${job.reportName} is ready to view`,
                severity: 'success',
              },
            });
          } else if (['F', 'N', 'X', 'T'].includes(log?.CRLOG_STATUS ?? '')) {
            // F failed, N no rows, X cancelled by an admin, T stopped at the time limit
            const code = log!.CRLOG_STATUS;
            const failed = code !== 'N';
            reportJobs.patch(job.id, { status: failed ? 'failed' : 'empty' });
            dispatch({
              type: 'OPEN_SNACK_TOAST',
              payload: {
                title: { F: 'Report failed', N: 'No data', X: 'Report cancelled', T: 'Report stopped' }[code],
                message:
                  code === 'N'
                    ? `${job.reportName} has no rows for the chosen filters`
                    : code === 'X'
                      ? `${job.reportName} was cancelled by an administrator`
                      : code === 'T'
                        ? `${job.reportName} ran past the time limit and was stopped; narrow the filters and try again`
                        : `${job.reportName} could not be generated`,
                severity: failed ? 'error' : 'info',
              },
            });
          } else if (now - job.submittedAt > JOB_EXPIRY_MS) {
            reportJobs.patch(job.id, { status: 'expired' });
          }
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp]);

  return pending.length;
};

const JobRow = ({ job, now }: { job: ReportJob; now: number }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const ready = job.status === 'ready';
  const expired = job.status === 'expired';
  const failed = job.status === 'failed';
  const empty = job.status === 'empty';
  const slow = job.status === 'queued' && now - job.submittedAt > 10 * 60 * 1000;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: ready && !job.seen ? alpha(theme.palette.success.main, 0.05) : 'transparent',
      }}
    >
      <Box sx={{ pt: '2px' }}>
        {ready ? (
          <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 22 }} />
        ) : failed ? (
          <ErrorOutlineRoundedIcon sx={{ color: 'error.main', fontSize: 22 }} />
        ) : empty ? (
          <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 22 }} />
        ) : expired ? (
          <ScheduleRoundedIcon sx={{ color: 'text.disabled', fontSize: 22 }} />
        ) : (
          <CircularProgress size={20} thickness={5} sx={{ m: '1px' }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }} noWrap title={job.reportName}>
          {job.reportName}
        </Typography>
        <Typography sx={{ fontSize: '0.74rem', color: failed ? 'error.main' : slow ? 'warning.main' : 'text.secondary' }}>
          {ready
            ? `Ready · ${dayjs(job.submittedAt).fromNow()}`
            : failed
              ? 'Could not be generated. Try again; if it fails again, contact IT.'
              : empty
                ? 'No rows for these filters.'
                : expired
              ? 'No result after 2 hours. Check the report logs.'
              : slow
                ? `Taking longer than usual · ${elapsed(job.submittedAt, now)}`
                : `Generating · ${elapsed(job.submittedAt, now)}`}
        </Typography>
        {ready && job.logId && (
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() =>
                reportJobs.openViewer({
                  logId: job.logId!,
                  reportName: job.reportName,
                  pdfUrl: job.pdfUrl,
                  excelUrl: job.excelUrl,
                })
              }
              sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, py: 0.25 }}
            >
              View
            </Button>
            {job.pdfUrl && (
              <Tooltip title="Download PDF">
                <IconButton size="small" href={job.pdfUrl} target="_blank" rel="noopener">
                  <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: 'error.main' }} />
                </IconButton>
              </Tooltip>
            )}
            {job.excelUrl && (
              <Tooltip title="Download Excel">
                <IconButton size="small" href={job.excelUrl} target="_blank" rel="noopener">
                  <TableChartOutlinedIcon sx={{ fontSize: 18, color: 'success.main' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {job.encId && (
          <Tooltip title="Open report page">
            <IconButton size="small" onClick={() => navigate(`/employee/reports/${job.encId}`)}>
              <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {(job.status !== 'queued' || slow) && (
          <Tooltip title={job.status === 'queued' ? 'Stop tracking' : 'Remove'}>
            <IconButton size="small" onClick={() => reportJobs.remove(job.id)}>
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ReportJobsTray = () => {
  const theme = useTheme();
  const { loggedUserData } = useAppSelector((state) => state.loginReducer);
  const jobs = useReportJobs((s) => s.jobs);
  const viewerOpen = useReportJobs((s) => !!s.viewer);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    reportJobs.init(loggedUserData?.user_code ?? null);
  }, [loggedUserData?.user_code]);

  const pendingCount = useJobPolling();
  const unseen = jobs.filter((j) => j.status === 'ready' && !j.seen).length;

  // live elapsed timers while something is running or the panel is open
  useEffect(() => {
    if (!pendingCount && !open) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [pendingCount, open]);

  // pop the panel open when a report finishes
  const prevUnseen = useRef(unseen);
  useEffect(() => {
    if (unseen > prevUnseen.current) setOpen(true);
    prevUnseen.current = unseen;
  }, [unseen]);

  // the full-screen viewer covers the page; the tray would float over its table
  if (!jobs.length || viewerOpen) return <ReportViewer />;

  const label = pendingCount
    ? `${pendingCount} report${pendingCount > 1 ? 's' : ''} generating`
    : unseen
      ? `${unseen} report${unseen > 1 ? 's' : ''} ready`
      : 'Reports';

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={() => {
          setOpen((o) => !o);
          setNow(Date.now());
        }}
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: theme.zIndex.snackbar - 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          pl: 1.25,
          pr: 2,
          py: 1,
          borderRadius: '999px',
          bgcolor: '#0F1B2D',
          color: '#fff',
          boxShadow: '0 10px 30px rgba(15,27,45,.35)',
          transition: 'transform .15s ease',
          '&:hover': { transform: 'translateY(-2px)' },
          ...(unseen && !pendingCount
            ? {
                animation: 'reportPulse 1.8s ease-out 3',
                '@keyframes reportPulse': {
                  '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.55)}` },
                  '100%': { boxShadow: `0 0 0 14px ${alpha(theme.palette.success.main, 0)}` },
                },
              }
            : {}),
        }}
      >
        <Badge color="success" variant="dot" invisible={!unseen}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,.1)',
            }}
          >
            {pendingCount ? (
              <CircularProgress size={16} thickness={6} sx={{ color: '#5EE08F' }} />
            ) : (
              <AssessmentRoundedIcon sx={{ fontSize: 18, color: '#5EE08F' }} />
            )}
          </Box>
        </Badge>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{label}</Typography>
      </ButtonBase>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top-end"
        transition
        sx={{ zIndex: theme.zIndex.snackbar }}
        modifiers={[{ name: 'offset', options: { offset: [0, 10] } }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={160}>
            <Paper
              elevation={0}
              sx={{
                width: 360,
                maxWidth: 'calc(100vw - 32px)',
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 20px 48px rgba(16,24,40,.18)',
                overflow: 'hidden',
              }}
            >
              <ClickAwayListener
                onClickAway={(e) => {
                  if (anchorRef.current?.contains(e.target as Node)) return;
                  setOpen(false);
                  reportJobs.markAllSeen();
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                        Background reports
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                        Keep working. We'll let you know when they're ready.
                      </Typography>
                    </Box>
                    {jobs.some((j) => j.status !== 'queued') && (
                      <Button
                        size="small"
                        onClick={reportJobs.clearFinished}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Clear done
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                    {jobs.map((job) => (
                      <JobRow key={job.id} job={job} now={now} />
                    ))}
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>

      <ReportViewer />
    </>
  );
};

export default ReportJobsTray;
